// How the Workspace talks to VERA.
//  - sample: deterministic replies, for design reviews and development.
//  - live:   VeraControlPlane POST /v1/orchestrate, with the signed-in person's Microsoft token.
// Either way, the reply carries the specialists VERA used and any actions it proposes (never executed here).

import { config } from "../config/env";
import { CONTROL_PLANE_WORKSPACE, type ScopeId } from "../config/model";
import { detectArtifact, type ArtifactKind } from "../lib/artifacts";
import { detectTools, type ToolId } from "../lib/tools";
import { devMode } from "../auth/auth";
import { honestVera } from "./honest";
import { currentLocation } from "../lib/location";
import { runTool, clientNow, TOOL_SOURCE, TOOL_STEPS, type ToolCall, type ToolResult } from "../lib/m365Tools";
import type { DocSpec, FileFormat } from "../lib/docgen";
import type { PdfEditOp } from "../lib/pdfedit";

export type Proposal = { id: string; description: string; requiresApproval: boolean };
/** An email VERA drafted. It is sent only when the person presses Send and says Yes in Windows. */
/** Files VERA looked up that this email should carry, each a reference the chat resolves to bytes (see resolveFileRefs). */
export type EmailAttachRef = { source: "file" | "email"; name?: string; drive_id?: string; item_id?: string; message_id?: string; attachment_id?: string };
export type EmailAction = { to: string[]; cc: string[]; subject: string; body: string; replyToMessageId?: string | null; attachFiles: boolean; attachRefs?: EmailAttachRef[] };
/** A meeting VERA set up. It is booked only when the person presses Book and says Yes in Windows. */
export type MeetingAction = { subject: string; start: string; minutes: number; attendees: string[]; agenda?: string | null; teams: boolean; location?: string | null };
/** A file VERA prepared. It is generated on this computer when the person downloads it. */
export type DocumentAction = DocSpec & { preferredFormat?: FileFormat | null };
export type PdfEditAction = { fileName: string; outputName?: string | null; summary?: string | null; ops: PdfEditOp[] };
/** Cancel (or decline) or move a meeting already in the calendar. */
export type MeetingChangeAction = { eventId: string; change: "cancel" | "move"; subject?: string | null; start?: string | null; minutes?: number | null; message?: string | null };
export type VeraAction = { id: string; type: "email" | "meeting" | "meeting_change" | "document" | "pdfedit"; email?: EmailAction; meeting?: MeetingAction; meetingChange?: MeetingChangeAction; document?: DocumentAction; pdfEdit?: PdfEditAction; done?: "sent" | "saved" | "booked" | "changed" | "declined" };
/** A file given to VERA. `text` for text files; `dataUrl` for images and documents (sent with the question, never stored in history). */
export type Attachment = {
  name: string; text: string;
  kind?: "text" | "image" | "document"; mime?: string; size?: number;
  dataUrl?: string; thumb?: string; width?: number; height?: number;
};
/** What a conversation keeps about a file: its name, type and a small thumbnail. */
export type FileRef = { name: string; kind: "text" | "image" | "document"; thumb?: string; size?: number };
export type Source = { title: string; url: string };
export type Card =
  | { type: "weather"; place: string; now: string; summary: string; days: { day: string; high: string; low: string; sky: string }[] }
  | { type: "traffic"; route: string; minutes: number; usual: number; note: string };
export type ChatMessage = {
  role: "user" | "vera"; text: string; at?: number;
  files?: FileRef[]; tools?: ToolId[];
  specialists?: string[]; steps?: string[]; sources?: Source[]; cards?: Card[];
  proposals?: Proposal[]; artifact?: ArtifactKind; slots?: string[]; sample?: boolean;
  actions?: VeraAction[];
};
export type AskInput = {
  text: string; scope: ScopeId; attachments?: Attachment[]; tools?: ToolId[];
  /** Earlier messages in this conversation, so "reply to that" works. */
  history?: { role: "user" | "vera"; text: string }[];
  /** What this app can do for VERA: "mail" and "calendar" (the person's own Microsoft 365), "documents" (make files). */
  clientTools?: string[];
  /** Called with what VERA is doing while she looks things up. */
  onStep?: (step: string) => void;
};
export type VeraReply = {
  text: string; specialists: string[]; proposals: Proposal[];
  steps?: string[]; sources?: Source[]; cards?: Card[]; artifact?: ArtifactKind; slots?: string[]; sample?: boolean;
  actions?: VeraAction[];
};

export interface VeraApi {
  mode: "sample" | "live";
  ask(input: AskInput, getToken: () => Promise<string | null>): Promise<VeraReply>;
}

const SAMPLE_REPLIES: Record<ScopeId, string> = {
  all: "Short version: Consumer 2.0 is closest to shipping, Marine is waiting on one credential, and VDS is being planned. Which one do you want to move today?",
  consumer: "Consumer 2.0: the web app is ready, PR #142 needs one fix, then Elsa can submit to App Store review. I can draft the reviewer notes.",
  vds: "VDS: VERA for construction teams, with My Day, Workspace and Operations. Want me to draft the product brief?",
  marine: "Marine: everything is running except Service Fusion, which needs fresh credentials from Carolyn. I can draft a short, friendly request to her.",
  aecch: "Ein Meilenstein ist verspätet, eine Akonto-Rechnung ist überfällig und ein schwerer Mangel ist offen. Womit fangen wir an?",
  aecus: "One milestone is late, one pay app is overdue and a major defect is open. Where should we start?",
  dama: "Two people are on duty, one issue is open at the main entrance, and this month's hour report is ready to draft. Where should I start?",
  veraai: "VERA Core is healthy. The Partner API is built and tested, and waiting to be deployed on Azure.",
  edutask: "EduTask: I can help you find funding, draft a proposal with its budget, or prepare an evaluation or skills needs assessment. Where would you like to start?",
  investor: "Here's the investor picture: Consumer V1 is live on both app stores, 2.0 is close to App Store review, and two enterprise platforms are in development with partners. The full Q4 update is in Files.",
};

const ARTIFACT_REPLIES: Record<ArtifactKind, string> = {
  raci: "Done. One person is Accountable for each task, and Elsa carries most of the delivery, so I kept Art and Monika to the reviews only they can do.",
  timetable: "Here's a six-week plan. The App Store review is the slowest step, so it starts in week 3 with a week of buffer before launch.",
  matrix: "I sorted your open items. Three need you today; two can go to Anand and Elsa.",
  status: "Here's this week's status, written for the leadership team. Every claim is linked to its source.",
  meeting: "Brief ready, built from Monday's invite and the latest updates.",
  email: "Drafted in your usual warm, direct tone. It stays in your drafts until you send it.",
  meet: "I checked everyone's calendars. These times work for all of them. Pick one and I'll send the Teams invite with an agenda.",
  planner: "Done. I created 7 tasks in the launch plan in Planner, each with its owner and due date from the RACI and timetable.",
};

/** The steps VERA shows while she works, from the tools she'll use and what she was given. */
export function plannedSteps(text: string, attachments: Attachment[] = [], _tools: ToolId[] = []): string[] {
  const images = attachments.filter((a) => a.kind === "image").length;
  const docs = attachments.length - images;
  const steps = ["Understanding your request"];
  if (docs) steps.push(`Reading ${docs} file${docs > 1 ? "s" : ""}`);
  if (images) steps.push(`Looking at ${images} image${images > 1 ? "s" : ""}`);
  if (/\b(pdf|word|docx|excel|xlsx|spreadsheet|powerpoint|pptx|deck|slides|file|document)\b/i.test(text)) steps.push("Preparing your file");
  steps.push("Writing the answer");
  return steps;
}

const SAMPLE_NOTE = "This is a sample answer, so you can see how it will look. Once VERA is connected live, this comes from the real service.";

export const sampleVera: VeraApi = {
  mode: "sample",
  async ask({ text, scope, attachments = [], tools = [] }) {
    const steps = plannedSteps(text, attachments, tools);
    await new Promise((r) => setTimeout(r, 500 + steps.length * 350));
    const wantsCode = tools.includes("code") || /\b(code|python|script|javascript|typescript|sql)\b/i.test(text);
    const kind = wantsCode ? null : detectArtifact(text);
    if (kind) {
      return {
        text: ARTIFACT_REPLIES[kind], steps,
        specialists: kind === "meet" ? ["Calendar", "Teams"] : ["Planning", "Team directory", "Documents"],
        proposals: [], sample: true,
        artifact: kind === "meet" || kind === "planner" ? undefined : kind,
        slots: kind === "meet" ? ["Tue 10:00 to 10:30", "Wed 14:00 to 14:30", "Thu 09:30 to 10:00"] : undefined,
      };
    }
    const images = attachments.filter((a) => a.kind === "image");
    const docs = attachments.filter((a) => a.kind === "document");
    const texts = attachments.filter((a) => !a.kind || a.kind === "text");
    const use = tools.length ? tools : detectTools(text, images.length > 0, docs.length > 0);

    if (use.includes("maps") && /traffic|commute|drive|route|eta/i.test(text)) {
      return { text: `Traffic is lighter than usual right now. Leave in the next 20 minutes to be safe.\n\n_${SAMPLE_NOTE}_`, steps, specialists: ["Azure Maps"], proposals: [], sample: true,
        cards: [{ type: "traffic", route: "St. Petersburg to Tampa International", minutes: 31, usual: 36, note: "Light traffic on I-275 over the Howard Frankland Bridge" }] };
    }
    if (use.includes("maps")) {
      return { text: `A warm, mostly sunny week with a chance of afternoon storms midweek, so plan outdoor meetings for the morning.\n\n_${SAMPLE_NOTE}_`, steps, specialists: ["Azure Maps"], proposals: [], sample: true,
        cards: [{ type: "weather", place: "St. Petersburg, FL", now: "84°F, partly cloudy", summary: "Sample forecast",
          days: [{ day: "Sat", high: "88°", low: "75°", sky: "Sunny" }, { day: "Sun", high: "87°", low: "76°", sky: "Partly cloudy" }, { day: "Mon", high: "86°", low: "75°", sky: "Storms after 3 pm" }, { day: "Tue", high: "87°", low: "74°", sky: "Sunny" }] }] };
    }
    if (use.includes("code")) {
      return { text: "Here's a small Python check that every task in a RACI has exactly one Accountable person, which is the rule VERA follows when she builds one for you:\n\n```python\nimport csv\n\ndef check_raci(path):\n    problems = []\n    with open(path, newline=\"\", encoding=\"utf-8\") as f:\n        for row in csv.DictReader(f):\n            task = row.pop(\"Task\")\n            owners = [name for name, role in row.items() if role.strip().upper() == \"A\"]\n            if len(owners) != 1:\n                problems.append(f\"{task}: {len(owners)} Accountable\")\n    return problems\n\nprint(check_raci(\"raci.csv\") or \"Every task has one owner.\")\n```\n\nIn live mode VERA can also **run** code like this in a safe sandbox and show you the result.", steps, specialists: ["Code"], proposals: [], sample: true };
    }
    if (use.includes("web")) {
      return { text: `Here's what I found, with sources you can check.\n\n- VERA Consumer is live on the **App Store** and **Google Play**.\n- The public site, veraneural.com, has the privacy policy and terms.\n\n_${SAMPLE_NOTE} Live web search reads current pages and cites each one._`, steps, specialists: ["Web search"], proposals: [], sample: true,
        sources: [{ title: "VERANeural", url: "https://veraneural.com" }, { title: "VERA on the App Store", url: "https://apps.apple.com/app/id6756195287" }, { title: "VERA on Google Play", url: "https://play.google.com/store/apps/details?id=ai.veraneural.app" }] };
    }
    if (use.includes("browser")) {
      return { text: `I opened the page and checked it.\n\n- The page loads, with no errors.\n- The App Store and Google Play links work.\n- The contact form is reachable.\n\n_${SAMPLE_NOTE}_`, steps, specialists: ["Browser check"], proposals: [], sample: true };
    }
    if (attachments.length) {
      const parts: string[] = [];
      if (texts.length) {
        const words = texts.reduce((n, a) => n + a.text.split(/\s+/).filter(Boolean).length, 0);
        parts.push(`I read ${texts.length === 1 ? `**${texts[0].name}**` : `${texts.length} files`}, about ${words.toLocaleString()} word${words === 1 ? "" : "s"}.`);
      }
      if (images.length) parts.push(`I can see ${images.length === 1 ? `**${images[0].name}**${images[0].width ? ` (${images[0].width} × ${images[0].height})` : ""}` : `${images.length} images`}. Live, I describe images, read any text in them and answer questions about them.`);
      if (docs.length) parts.push(`${docs.length === 1 ? `**${docs[0].name}**` : `${docs.length} documents`} will be read by Document Intelligence when VERA is live, including tables, forms and scans.`);
      parts.push(`_${SAMPLE_NOTE}_`);
      return { text: parts.join("\n\n"), steps, specialists: ["Documents"], proposals: [], sample: true };
    }
    return { text: SAMPLE_REPLIES[scope] ?? SAMPLE_REPLIES.all, steps, specialists: ["Projects", "Tracker", "Email"], proposals: [], sample: true };
  },
};

type OrchestrationResult = {
  synthesis?: string;
  specialistsInvoked?: string[];
  proposedActions?: { actionId: string; description: string; requiresApproval: boolean; executed: boolean }[];
  // Added for Studio (see docs/vera-capabilities.md). Older control planes leave these out.
  steps?: string[];
  citations?: Source[];
  cards?: Card[];
  /** Read-only lookups VERA wants run here, with the person's own Microsoft sign-in. */
  toolCalls?: ToolCall[];
  /** Emails and meetings VERA proposes. Nothing happens until the person confirms. */
  actions?: VeraAction[];
};

export const MAX_LOOKUP_ROUNDS = 5;

export const liveVera: VeraApi = {
  mode: "live",
  async ask({ text, scope, attachments, tools, history, clientTools, onStep }, getToken) {
    const target = CONTROL_PLANE_WORKSPACE[scope];
    if (!target) throw new Error("This workspace isn't connected to VERA's control plane yet. Ask Elsa to add it.");
    const token = await getToken();
    if (!token) throw new Error("Please sign in again so VERA can verify who you are.");
    const textFiles = (attachments ?? []).filter((a) => !a.kind || a.kind === "text");
    const request = textFiles.length
      ? `${text}\n\n${textFiles.map((a) => `<attached_file name="${a.name}">\n${a.text.slice(0, 20000)}\n</attached_file>`).join("\n")}`
      : text;
    // Images and documents go as data the control plane hands to vision and Document Intelligence.
    const binary = (attachments ?? []).filter((a) => a.kind === "image" || a.kind === "document")
      .map((a) => ({ name: a.name, kind: a.kind, mimeType: a.mime, dataUrl: a.dataUrl }));
    // Where they are, only when they switched Location on (see lib/location).
    const here = await currentLocation();
    const location = here ? { lat: here.lat, lon: here.lon, accuracy: here.accuracy, at: here.at } : undefined;
    const client = clientTools?.length || location ? { tools: clientTools ?? [], timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, now: clientNow(), location } : undefined;
    const toolRounds: { calls: ToolCall[]; results: ToolResult[] }[] = [];
    // What VERA actually used, shown under her answer.
    const used = new Set<string>();
    const worked: string[] = [];
    let data: OrchestrationResult;
    for (let round = 0; ; round++) {
      const res = await fetch(`${config.controlPlaneUrl}/v1/orchestrate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          request, workspaceId: target.workspaceId, targetVertical: target.vertical,
          attachmentName: attachments?.[0]?.name, attachments: binary.length ? binary : undefined,
          tools: tools?.length ? tools : undefined, // empty = VERA decides
          history: history?.length ? history.slice(-10) : undefined,
          client, toolRounds: toolRounds.length ? toolRounds : undefined,
          previewOnly: true,
        }),
      });
      if (res.status === 401 || res.status === 403) throw new Error("You don't have access to this workspace. Ask an admin to check your role.");
      if (res.status === 400) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error || "VERA couldn't use that. Please try again.");
      if (!res.ok) throw new Error("VERA is unavailable right now. Please try again in a moment.");
      data = (await res.json()) as OrchestrationResult;
      if (!data.toolCalls?.length || !client || round >= MAX_LOOKUP_ROUNDS) break;
      // VERA asked to look something up: run it here, with the person's own sign-in, and send her what was found.
      const results: ToolResult[] = [];
      for (const call of data.toolCalls) {
        const step = TOOL_STEPS[call.name] ?? "Looking that up";
        onStep?.(step);
        if (!worked.includes(step)) worked.push(step);
        if (TOOL_SOURCE[call.name]) used.add(TOOL_SOURCE[call.name]);
        results.push(await runTool(call));
      }
      toolRounds.push({ calls: data.toolCalls, results });
    }
    return {
      text: data.synthesis?.trim() || "I couldn't put together an answer this time. Try rephrasing?",
      specialists: [...new Set([...(data.specialistsInvoked ?? []), ...used, ...(data.actions?.some((a) => a.type === "document") ? ["Documents"] : [])])],
      steps: data.steps?.length ? data.steps : worked.length ? worked : undefined,
      sources: data.citations,
      cards: data.cards,
      proposals: (data.proposedActions ?? []).filter((p) => !p.executed).map((p) => ({ id: p.actionId, description: p.description, requiresApproval: p.requiresApproval })),
      // Live answers never show sample tables: real files come from VERA as document actions.
      actions: data.actions?.length ? data.actions.map((a) => ({ id: a.id, type: a.type, email: a.email ?? undefined, meeting: a.meeting ?? undefined, document: a.document ?? undefined, pdfEdit: a.pdfEdit ?? undefined })) : undefined,
    };
  },
};

/**
 * Which VERA answers:
 *  - live: the control plane (VERA's full intelligence).
 *  - the demo (sample people): sample answers, clearly tagged.
 *  - the team version before going live: honest VERA, who shows real connected data or says what she can't do yet. Never pretends.
 */
export const vera: VeraApi = {
  get mode() { return config.dataMode === "live" ? "live" : "sample"; },
  ask: (input, getToken) => {
    if (config.dataMode !== "live") return (devMode ? sampleVera : honestVera).ask(input, getToken);
    // Live: the person's own Microsoft 365 (mail, calendar, Planner, Teams, files) is read directly with their sign-in;
    // everything else goes to VERA's intelligence on the control plane.
    // When VERA can look up their Microsoft 365 herself, she answers and reasons over it.
    return (readsOwnMicrosoft365(input.text) && !input.attachments?.length && !input.clientTools?.includes("mail") ? honestVera : liveVera).ask(input, getToken);
  },
};

/** Requests that only read the person's own Microsoft 365, answered from Graph with their sign-in. */
export function readsOwnMicrosoft365(text: string): boolean {
  if (/\b(draft|write|reply|respond|send|summari[sz]e|analy[sz]e|why|plan|prepare)\b/i.test(text)) return false;
  return /\b(inbox|e-?mails?|my mail|calendar|my day|meetings? today|agenda|planner|my tasks|overdue|teams chats?|my chats|recent files|sharepoint|onedrive)\b/i.test(text);
}
